'use client';

import type { HTMLAttributes } from 'react';
import { createElement } from 'react';
import { cx } from '../utils/cx';

export type TextTag = 'p' | 'span' | 'div';

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: TextTag;
  text?: string;
  size?: string;
  tone?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'on-brand';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  align?: 'left' | 'center' | 'right';
  className?: string;
}

const toneMap: Record<NonNullable<TextProps['tone']>, string> = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  tertiary: 'text-tertiary',
  quaternary: 'text-quaternary',
  'on-brand': 'text-on-brand',
};

const weightMap: Record<NonNullable<TextProps['weight']>, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const alignMap: Record<NonNullable<TextProps['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function Text({
  as = 'p',
  text,
  size = 'text-sm leading-6',
  tone = 'tertiary',
  weight = 'normal',
  align = 'left',
  className,
  children,
  ...rest
}: TextProps) {
  const content = children ?? text ?? null;

  return createElement(
    as,
    {
      ...rest,
      className: cx(size, toneMap[tone], weightMap[weight], alignMap[align], className),
    },
    content,
  );
}

Text.displayName = 'Text';